import { Stack, TextField } from '@mui/material'
import { ArrowUpRight, Check, ClipboardText, Files, X } from '@phosphor-icons/react'
import type { ExternalParticipant } from '@einwert/valuation-api-client'
import { ThreadWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { getThreadType, TextWithEllipsis, theme, ThreadAvatar, TinyButton, tokens, User } from 'ComponentLibrary'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { BORDER_WIDTH, THREAD_HEADER_HEIGHT } from '../constants'
import type { AppraiserUser, CustomerUser } from '@einwert/valuation-api-client'
import MessageListHeaderThreadParticipants from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageListHeaderThreadParticipants'
import MessageListHeaderOptionsMenu from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageListHeaderOptionsMenu'
import { useTaskSideDrawer } from 'ComponentLibrary/src/context/TaskSideDrawerContext'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'

type MessageListHeaderProps = {
    thread: ThreadWithMetadata
    isPinned: boolean
    currentUser?: AppraiserUser | CustomerUser
    replaceTopic: (topic: string) => void
    replaceParticipantIds: (participantIds: number[]) => void
    isPatchingThread: boolean
    pinThread: (threadId: string) => void
    unpinThread: (threadId: string) => void
    setIsEditingParticipants: (isEditingParticipants: boolean) => void
    deleteThread: (threadId: string) => void
    isDeleteThreadPending: boolean
    orderObserverIds: string[] | undefined
    orderObservers: User[]
    externalParticipantUsers: ExternalParticipant[]
}

const MessageListThreadsHeader = ({
    thread,
    isPinned,
    currentUser,
    replaceTopic,
    replaceParticipantIds,
    isPatchingThread,
    pinThread,
    unpinThread,
    setIsEditingParticipants,
    deleteThread,
    isDeleteThreadPending,
    orderObserverIds,
    orderObservers,
    externalParticipantUsers,
}: MessageListHeaderProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const threadType = getThreadType(thread)
    const userLinkCopy = useUserLinkCopy()

    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false)
    const [topic, setTopic] = useState<string>('')
    const navigate = useNavigate()
    useEffect(() => {
        setTopic(thread.topic)
        setIsEditingTitle(false)
    }, [thread])

    const onSubmitTopic = () => {
        replaceTopic(topic)
        setIsEditingTitle(false)
    }

    const onCancel = () => {
        setIsEditingTitle(false)
        setTopic(thread.topic)
    }

    const { openTaskSideDrawer } = useTaskSideDrawer()

    const isThreadPending = isPatchingThread || isDeleteThreadPending

    return (
        <Stack
            direction="row"
            minHeight={THREAD_HEADER_HEIGHT}
            paddingLeft="24px"
            alignItems="center"
            borderBottom={`${BORDER_WIDTH} solid ${tokens.ColorBorderDefault}`}
        >
            <Stack direction="row" alignItems="center" gap={theme.spacing(4)} width="100%" paddingRight="60px">
                <ThreadAvatar type={threadType} />
                {!isEditingTitle ? (
                    <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
                        <TextWithEllipsis
                            typographyProps={{
                                variant: 'bodyTextMedium',
                                color: isThreadPending ? tokens.ColorTextDisabled : tokens.ColorTextBoldest,
                            }}
                            tooltipText={thread.topic}
                        >
                            {thread.topic || t('noTitle')}
                        </TextWithEllipsis>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing075}>
                            {isThreadPending && <LoadingIndicator title={t('saving')} isPending={isThreadPending} height={1} />}
                            {thread.associatedTask && (
                                <TinyButton
                                    label={t('taskDetails')}
                                    leftIcon={<ClipboardText size={tokens.SizingIconS} color={tokens.ColorIconLinkDefault} weight="bold" />}
                                    onClick={() => thread.associatedTask && openTaskSideDrawer(thread.associatedTask.uuid)}
                                />
                            )}

                            {thread.order_uuid && (
                                <TinyButton
                                    label={t('goToOrder')}
                                    leftIcon={<Files size={tokens.SizingIconS} color={tokens.ColorIconLinkDefault} weight="bold" />}
                                    rightIcon={<ArrowUpRight size={tokens.SizingIconS} color={tokens.ColorIconSubtle} weight="bold" />}
                                    onClick={() => navigate(`/orders/${thread.order_uuid}/overview`)}
                                />
                            )}
                            <MessageListHeaderThreadParticipants
                                orderObserverIds={orderObserverIds}
                                orderObservers={orderObservers}
                                externalParticipantUsers={externalParticipantUsers}
                                participantUsers={thread.participant_users}
                                participantIds={thread.participant_ids}
                                currentUser={currentUser}
                                replaceParticipantIds={replaceParticipantIds}
                                setIsEditingParticipants={setIsEditingParticipants}
                                userLinkCopy={userLinkCopy}
                            />
                            <MessageListHeaderOptionsMenu
                                thread={thread}
                                isPinned={isPinned}
                                currentUser={currentUser}
                                isPatchingThread={isPatchingThread}
                                pinThread={pinThread}
                                unpinThread={unpinThread}
                                onReplaceParticipantIds={replaceParticipantIds}
                                onEditParticipants={setIsEditingParticipants}
                                onEditTitle={setIsEditingTitle}
                                showTaskAndOrderNavigations
                                deleteThread={deleteThread}
                            />
                        </Stack>
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" gap={theme.spacing(2)}>
                        <TextField
                            autoFocus
                            sx={{ minWidth: '227px' }}
                            value={topic}
                            placeholder={t('titlePlaceholder')}
                            size="small"
                            onChange={(e) => setTopic(e.target.value)}
                            variant="outlined"
                        />
                        <IconButton size="small" variant="tertiary" icon={<X weight="bold" />} onClick={onCancel} />
                        <IconButton size="small" variant="primary" icon={<Check weight="bold" />} onClick={onSubmitTopic} />
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

export default MessageListThreadsHeader
